/**
 * Chat attachment upload via /chat/v2 attachments/presign (same wire as mobile chat app).
 * Computes ActiveStorage MD5 checksum, presigns, then PUT to storage.
 */

import SparkMD5 from 'spark-md5'
import { chatApi } from './chat-api.js'

const CHUNK_SIZE = 2_097_152 // 2MB — matches @rails/activestorage FileChecksum

function fileChecksum(file) {
  return new Promise((resolve, reject) => {
    const chunkCount = Math.ceil(file.size / CHUNK_SIZE) || 1
    let chunkIndex = 0
    const md5 = new SparkMD5.ArrayBuffer()
    const reader = new FileReader()

    reader.addEventListener('error', () => reject(new Error(`Error reading ${file.name}`)))
    reader.addEventListener('load', (event) => {
      md5.append(event.target.result)
      if (chunkIndex < chunkCount) {
        readNext()
      } else {
        resolve(btoa(md5.end(true)))
      }
    })

    function readNext() {
      const start = chunkIndex * CHUNK_SIZE
      const end = Math.min(start + CHUNK_SIZE, file.size)
      chunkIndex += 1
      reader.readAsArrayBuffer(file.slice(start, end))
    }

    readNext()
  })
}

/**
 * Upload a File via chat v2 presign → direct PUT.
 * @param {number} roomId
 * @param {File} file
 * @param {{ onProgress?: (pct: number) => void }} [opts]
 * @returns {Promise<string>} signed_id
 */
export async function uploadChatAttachment(roomId, file, { onProgress } = {}) {
  const checksum = await fileChecksum(file)
  const contentType = file.type || 'application/octet-stream'

  const presignRes = await chatApi.attachments.presign(roomId, {
    filename: file.name,
    byte_size: file.size,
    checksum,
    content_type: contentType,
  })
  if (!presignRes.ok) {
    let message = `Presign failed (${presignRes.statusCode})`
    try {
      const errBody = await presignRes.json
      message = errBody?.error || errBody?.errors?.[0] || message
    } catch { /* ignore */ }
    throw new Error(message)
  }

  const data = await presignRes.json
  const uploadUrl = data?.direct_upload?.url
  const uploadHeaders = data?.direct_upload?.headers || {}
  const signedId = data?.signed_id
  if (!uploadUrl || !signedId) {
    throw new Error('Invalid presign response')
  }

  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', uploadUrl, true)
    Object.entries(uploadHeaders).forEach(([k, v]) => xhr.setRequestHeader(k, v))
    if (!uploadHeaders['Content-Type'] && !uploadHeaders['content-type']) {
      xhr.setRequestHeader('Content-Type', contentType)
    }
    if (onProgress) {
      xhr.upload.addEventListener('progress', (ev) => {
        if (ev.lengthComputable) onProgress(Math.round((ev.loaded / ev.total) * 100))
      })
    }
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new Error(`Storage upload failed: ${xhr.status}`))
    })
    xhr.addEventListener('error', () => reject(new Error('Storage upload network error')))
    xhr.send(file)
  })

  return signedId
}
